import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { askReason } from "../ApprovalLock";
import { field, label, SplitRows, downloadDocPdf, cleanSplits, splitsTotal } from "./shared";

const toForm = (d) => ({
  date: d.date || "", amount: String(d.amount ?? ""),
  name: d.receiver_name || "", mobile: d.mobile || "",
  voucher_type: d.voucher_type || "", employee_name: d.employee_name || "", employee_id: d.employee_id || "",
  pay_method: d.pay_method || "cash", reference: d.reference || "",
  party_type: d.party_type || "credit", vehicle_number: d.vehicle_number || "", account_name: d.account_name || "",
  invoice_mode: d.invoice_mode || "random",
  invoice_no: d.allocations && d.allocations[0] && d.allocations[0].invoice_no !== "OPENING" ? d.allocations[0].invoice_no : "",
  splits: (d.splits && d.splits.length ? d.splits : [{ method: d.pay_method || "cash", amount: d.amount, reference: d.reference || "" }]).map((s) => ({ method: s.method, amount: String(s.amount ?? ""), reference: s.reference || "", bank_account: s.bank_account || "" })),
});

const validate = (doc, ef, splits, isCap) => {
  const voucher = doc.doc_type === "voucher";
  if (!ef.name.trim()) return voucher ? "Paid-to name required" : "Received-from name required";
  if (splits.length === 0) return "Enter at least one amount";
  if (splits.some((s) => s.method !== "cash" && !s.reference.trim())) return "Add a reference for non-cash methods";
  if (!voucher && isCap && !ef.name.trim()) return "Choose the capital partner";
  if (!voucher && (ef.party_type || "").toLowerCase() === "credit" && !(ef.account_name || "").trim()) return "Choose a credit account";
  if (voucher && ef.voucher_type === "Salary" && !ef.employee_id) return "Select the employee for a salary voucher";
  return "";
};

const putDoc = (doc, ef, splits, edit_reason) => {
  const amount = splitsTotal(splits);
  if (doc.doc_type === "voucher") {
    return api.put(`/vouchers/${doc.id}`, { date: ef.date, amount, receiver_name: ef.name.trim(), mobile: ef.mobile, voucher_type: ef.voucher_type, employee_name: ef.employee_name, employee_id: ef.voucher_type === "Salary" ? ef.employee_id : "", account_name: (ef.account_name || "").trim(), pay_method: splits[0].method, splits, reference: ef.reference, edit_reason });
  }
  const credit = (ef.party_type || "").toLowerCase() === "credit";
  return api.put(`/payment-receipts/${doc.id}`, { date: ef.date, amount, received_from: credit && ef.account_name.trim() ? ef.account_name.trim() : ef.name.trim(), mobile: ef.mobile, pay_method: splits[0].method, splits, reference: ef.reference, party_type: ef.party_type, account_name: (ef.account_name || "").trim(), vehicle_number: ef.vehicle_number, invoice_mode: ef.invoice_mode, invoice_no: ef.invoice_mode === "individual" ? ef.invoice_no : null, edit_reason });
};

const VoucherFields = ({ ef, setEf, emps }) => (
  <div className="grid grid-cols-2 gap-3">
    <div><label className={label}>Voucher type</label><input data-testid="edit-doc-vtype" className={field} value={ef.voucher_type} onChange={(e) => setEf({ ...ef, voucher_type: e.target.value })} /></div>
    <div><label className={label}>Employee{ef.voucher_type === "Salary" && <span className="text-destructive"> *</span>}</label>
      {ef.voucher_type === "Salary" ? (
        <select data-testid="edit-doc-emp" className={field} value={ef.employee_id || ""}
          onChange={(e) => { const emp = emps.find((x) => x.id === e.target.value); setEf({ ...ef, employee_id: e.target.value, employee_name: emp ? emp.name : "", name: emp ? emp.name : ef.name }); }}>
          <option value="">Select employee…</option>
          {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      ) : <input data-testid="edit-doc-emp" className={field} value={ef.employee_name} onChange={(e) => setEf({ ...ef, employee_name: e.target.value })} placeholder="optional" />}
    </div>
  </div>
);

const ReceiptFields = ({ ef, setEf }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <div><label className={label}>Vehicle</label><input data-testid="edit-doc-vehicle" className={field} value={ef.vehicle_number} onChange={(e) => setEf({ ...ef, vehicle_number: e.target.value })} placeholder="optional" /></div>
      <div><label className={label}>Allocation</label>
        <select data-testid="edit-doc-imode" className={field} value={ef.invoice_mode} onChange={(e) => setEf({ ...ef, invoice_mode: e.target.value })}>
          <option value="random">On account</option>
          <option value="individual">Individual invoice</option>
          <option value="bulk">Oldest invoices first</option>
        </select></div>
    </div>
    {ef.invoice_mode === "individual" && (
      <div><label className={label}>Invoice number</label><input data-testid="edit-doc-invno" className={field} value={ef.invoice_no} onChange={(e) => setEf({ ...ef, invoice_no: e.target.value })} placeholder="Invoice to settle" /></div>
    )}
    <p className="text-xs text-muted-foreground">Changing the amount or allocation re-posts this receipt to the cash book and the credit party ledger.</p>
  </>
);

const AccountFields = ({ doc, ef, setEf, isCap, caps, acctOpts }) => (
  <>
    {doc.doc_type !== "voucher" && (
      <label className="flex items-center gap-2 text-sm">
        <input data-testid="edit-doc-capital-toggle" type="checkbox" className="h-4 w-4" checked={isCap}
          onChange={(e) => setEf({ ...ef, party_type: e.target.checked ? "Capital Account" : "Cash", name: e.target.checked ? "" : ef.name, account_name: "" })} />
        Add to partner capital account
      </label>
    )}
    {isCap ? (
      <div><label className={label}>Capital partner <span className="text-destructive">*</span></label>
        <select data-testid="edit-doc-capital-partner" className={field} value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })}>
          <option value="">Select partner…</option>
          {caps.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-1">Money received = capital deposited (credit) to this partner.</p></div>
    ) : (
      <div>
        <label className={label}>{doc.doc_type !== "voucher" && (ef.party_type || "").toLowerCase() === "credit" ? <>Credit account <span className="text-destructive">*</span></> : "Account name"}</label>
        <input data-testid="edit-doc-account" list="edit-doc-account-list" className={field} value={ef.account_name || ""} onChange={(e) => setEf({ ...ef, account_name: e.target.value })} placeholder="Type to search account…" />
        <datalist id="edit-doc-account-list">{acctOpts.map((a) => <option key={a} value={a} />)}</datalist>
      </div>
    )}
  </>
);

const EditLog = ({ log }) => (log && log.length > 0 ? (
  <div className="rounded-md border border-border bg-muted/40 p-2.5">
    <div className="text-xs font-semibold mb-1">Edit history</div>
    <div className="space-y-1 max-h-28 overflow-y-auto">
      {log.slice().reverse().map((l, i) => (
        <div key={`${l.at}-${l.note}`} data-testid={`edit-log-${i}`} className="text-[11px] text-muted-foreground num">{(l.at || "").slice(0, 16).replace("T", " ")} · {l.by_name || "user"} — {l.note}</div>
      ))}
    </div>
  </div>
) : null);

const useEditLookups = (doc) => {
  const [acctOpts, setAcctOpts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [caps, setCaps] = useState([]);
  useEffect(() => { api.get("/capital-accounts").then((r) => setCaps(r.data || [])).catch(() => setCaps([])); }, []);
  useEffect(() => {
    if (!doc) return;
    api.get("/credit-accounts", { params: { include_all: true } }).then((r) => setAcctOpts([...new Set((r.data || []).map((a) => a.account))])).catch(() => setAcctOpts([]));
    if (doc.doc_type === "voucher") api.get("/employees").then((r) => setEmps(r.data || [])).catch((e) => console.error(e));
  }, [doc]);
  return { acctOpts, emps, caps };
};

export const EditDocDialog = ({ doc, onClose, onSaved, user, canApprove, reloadLocks }) => {
  const [ef, setEf] = useState(null);
  const { acctOpts, emps, caps } = useEditLookups(doc);
  useEffect(() => { setEf(doc ? toForm(doc) : null); }, [doc]);
  const isCap = (ef?.party_type || "").toLowerCase().startsWith("capital");

  const save = async (alsoPrint = false) => {
    const splits = cleanSplits(ef.splits || []);
    const edit_reason = askReason(user, "editing", doc.number, doc.doc_type !== "voucher");
    if (edit_reason === null) return;
    const err = validate(doc, ef, splits, isCap);
    if (err) return toast.error(err);
    try {
      const res = await putDoc(doc, ef, splits, edit_reason);
      if (res.data?.status === "pending") { toast.info(res.data.message || "Edit sent for approval"); onClose(); onSaved(); reloadLocks(); return; }
      toast.success(`${doc.number} updated`);
      onClose(); onSaved();
      if (alsoPrint) downloadDocPdf(doc.id, doc.number);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit {doc?.doc_type === "voucher" ? "Voucher" : "Receipt"} · {doc?.number}</DialogTitle></DialogHeader>
        {doc && ef && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3"><div><label className={label}>Date</label><input data-testid="edit-doc-date" type="date" className={field + " num"} value={ef.date} onChange={(e) => setEf({ ...ef, date: e.target.value })} /></div></div>
            <div><label className={label}>{doc.doc_type === "voucher" ? "Paid to" : "Received from"}</label><input data-testid="edit-doc-name" className={field} value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3"><div><label className={label}>Mobile</label><input data-testid="edit-doc-mobile" className={field} value={ef.mobile} onChange={(e) => setEf({ ...ef, mobile: e.target.value })} placeholder="optional" /></div></div>
            <SplitRows rows={ef.splits || []} setRows={(r) => setEf({ ...ef, splits: r })} prefix="edit-doc" partialOption={doc.doc_type !== "voucher"} target={doc.doc_type !== "voucher" && ef.invoice_mode === "individual" ? doc.amount : 0} />
            <AccountFields doc={doc} ef={ef} setEf={setEf} isCap={isCap} caps={caps} acctOpts={acctOpts} />
            {doc.doc_type === "voucher" ? <VoucherFields ef={ef} setEf={setEf} emps={emps} /> : <ReceiptFields ef={ef} setEf={setEf} />}
            <div><label className={label}>Reference / note</label><input data-testid="edit-doc-ref" className={field} value={ef.reference} onChange={(e) => setEf({ ...ef, reference: e.target.value })} placeholder="optional" /></div>
            {!canApprove && <p className="text-xs text-amber-600">Your edit will be sent to the admin for approval (you'll be asked for a reason).</p>}
            <EditLog log={doc.edit_log} />
          </div>
        )}
        <DialogFooter>
          <button data-testid="edit-doc-save" onClick={() => save(false)} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Save changes</button>
          {canApprove && (
            <button data-testid="edit-doc-save-print" onClick={() => save(true)} className="h-10 px-5 rounded-md border border-input bg-card font-medium flex items-center gap-2 hover:bg-muted transition-colors"><Download size={15} /> Save &amp; Print</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
